/**
 * Localized-file resolution for content packs. Build-for-English: a lesson's
 * `file` names the base (English) document; translated siblings follow the
 * convention `<base>.<lang>.<ext>` (e.g. `01-overview.md` → `01-overview.es.md`),
 * which is how the content repos already store en/es/pt triples. A pack advertises
 * the languages it supports via `manifest.languages`; the app picks one and the
 * loader reads the matching sibling (falling back to the base). See
 * CONTENT-PACK-SPEC §1.2.
 */

export const DEFAULT_LANGUAGE = "en";

/** The content languages the app ships. */
export const APP_LANGUAGES = ["en", "es", "pt"] as const;

/** A loose BCP-47-ish language tag: "en", "es", "pt-BR". */
const LANG_TAG = /^[a-z]{2}(?:-[A-Z]{2})?$/;

export function isLanguageTag(tag: string): boolean {
  return LANG_TAG.test(tag);
}

/**
 * Resolve a user's language preference to a concrete app language (one of
 * APP_LANGUAGES). `pref` is "system" or an explicit "en"/"es"/"pt":
 *  - "system" → the device tag's primary subtag ("es-MX" → "es") if the app ships
 *    it, otherwise English.
 *  - an explicit language → itself if shipped, otherwise English.
 */
export function resolveLanguagePref(
  pref: string | undefined,
  deviceTag: string | undefined,
): string {
  const shipped: readonly string[] = APP_LANGUAGES;
  if (pref && pref !== "system") {
    return shipped.includes(pref) ? pref : DEFAULT_LANGUAGE;
  }
  const primary = (deviceTag ?? "").toLowerCase().split(/[-_]/)[0];
  return shipped.includes(primary) ? primary : DEFAULT_LANGUAGE;
}

/**
 * Map a base file path to its localized sibling for `lang`. The default language
 * returns the path unchanged; other languages insert `.<lang>` before the final
 * extension (`quiz.json` → `quiz.es.json`). A path with no extension just gets
 * `.<lang>` appended.
 */
export function localizedFile(file: string, lang: string, defaultLang = DEFAULT_LANGUAGE): string {
  if (lang === defaultLang) return file;
  const slash = file.lastIndexOf("/");
  const dot = file.lastIndexOf(".");
  // A dot only counts as an extension separator if it's after the last slash.
  if (dot <= slash) return `${file}.${lang}`;
  return `${file.slice(0, dot)}.${lang}${file.slice(dot)}`;
}

/**
 * Choose the language to render given the user's preference and what a pack
 * supports. Falls back to the pack's default (or English), then to the first
 * supported language, so rendering always resolves to something.
 */
export function pickLanguage(
  preferred: string | undefined,
  supported: string[] | undefined,
  defaultLang = DEFAULT_LANGUAGE,
): string {
  const langs = supported && supported.length > 0 ? supported : [defaultLang];
  if (preferred && langs.includes(preferred)) return preferred;
  if (langs.includes(defaultLang)) return defaultLang;
  return langs[0];
}

/**
 * Per-view "translate this content" override (docs/035-PER-CONTENT-LANGUAGE.md): a
 * screen-local choice (carried in the route's `lang` param) layers over the app's
 * global content-language preference, without writing it. An explicit non-empty
 * override always wins; `undefined`/`null`/`""` means "no override" → global.
 */
export function effectiveLanguage(override: string | undefined | null, global: string): string {
  return override && override.length > 0 ? override : global;
}

/**
 * Per-*pack* language persistence (docs/035-PER-CONTENT-LANGUAGE.md v1): resolve the
 * effective language for a pack/lesson screen when there's a **persisted** last-used
 * language for that pack, layered between the route `lang` param and the global
 * `contentLanguage`. Precedence: explicit route param (non-empty) wins outright;
 * otherwise a persisted per-pack language; otherwise the global default. Composes with
 * `effectiveLanguage` — `resolvePackLanguage(p, persisted, g) === effectiveLanguage(p, effectiveLanguage(persisted, g))`.
 */
export function resolvePackLanguage(
  paramLang: string | undefined | null,
  persistedLang: string | undefined | null,
  global: string,
): string {
  return effectiveLanguage(paramLang, effectiveLanguage(persistedLang, global));
}

/**
 * Whether a content screen should offer to adopt the CURRENT Settings default language
 * (docs/254-LANGUAGE-CHANGE-PROPAGATION-AND-PIN.md v0) — the "Switch to {default}?"
 * affordance shown when a per-tab/per-pack language **override** is pinning a view to a
 * language that used to be, but no longer is, the app's default (e.g. Settings was
 * changed to English while a tab sat overridden to Spanish). `overrideLang` is the
 * screen's own currently-EFFECTIVE language (whatever resolved it — a route `lang`
 * param, a persisted per-pack choice, or both via `resolvePackLanguage`); `show` is
 * true only when that's present and differs from `defaultLang`, in which case `toLang`
 * is `defaultLang` — the language a tap should adopt.
 */
export function defaultLanguageSwitchOffer(
  overrideLang: string | undefined,
  defaultLang: string,
): { show: boolean; toLang?: string } {
  if (!overrideLang || overrideLang.length === 0 || overrideLang === defaultLang) {
    return { show: false };
  }
  return { show: true, toLang: defaultLang };
}

/**
 * `Settings.languageChangeBehavior` (docs/254-LANGUAGE-CHANGE-PROPAGATION-AND-PIN.md's "A
 * setting for HOW a default-language change is handled"): which of the two owner-offered
 * options governs a pinned view once the Settings default moves out from under it.
 * `"ask"` (default) — the per-page `LanguageSwitchBanner` offer; `"auto"` — a silent
 * tab-store sweep clears every pinned tab's override instead.
 */
export type LanguageChangeBehavior = "ask" | "auto";
export const LANGUAGE_CHANGE_BEHAVIORS: LanguageChangeBehavior[] = ["ask", "auto"];

/**
 * The single policy chokepoint both the per-page banner and the tab-store sweep read
 * from (docs/254-LANGUAGE-CHANGE-PROPAGATION-AND-PIN.md): given the user's
 * `behavior` preference and whether THIS view is pinned off the current default
 * (same `overrideLang`/`defaultLang` shape as {@link defaultLanguageSwitchOffer}) —
 * - `"none"` — no override, or the override already matches the default: nothing to do.
 * - `"showOffer"` — `behavior === "ask"` and the view is pinned: show the banner.
 * - `"autoClear"` — `behavior === "auto"` and the view is pinned: clear the override
 *   without asking (the caller — a tab-store sweep or the active screen itself —
 *   performs the actual clear; this function only decides that it should).
 */
export function resolveLanguageChangeAction(
  behavior: LanguageChangeBehavior,
  overrideLang: string | undefined,
  defaultLang: string,
): "showOffer" | "autoClear" | "none" {
  if (!defaultLanguageSwitchOffer(overrideLang, defaultLang).show) return "none";
  return behavior === "auto" ? "autoClear" : "showOffer";
}

/**
 * Languages a translate control should offer for one piece of content: the
 * intersection of what it advertises (`advertised` — a pack's `manifest.languages`,
 * a catalog's/catalog entry's `languages`, or a per-lesson probe result) with the
 * app's shipped languages, deduped, in shipped order. Fewer than 2 results means
 * "nothing to translate to" — callers hide the control in that case.
 */
export function availableLanguages(
  advertised: readonly string[] | undefined,
  shipped: readonly string[] = APP_LANGUAGES,
): string[] {
  if (!advertised || advertised.length === 0) return [];
  const set = new Set(advertised);
  return shipped.filter((l) => set.has(l));
}
